import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import Inputmask from "jquery.inputmask/dist/inputmask/inputmask";
import Select from "rc-select";
import "rc-select/assets/index.css";

interface PhoneProps {
	label: string;
	deletableField?: boolean;
	disabled?: boolean;
	model: any;
	bindingProperty: any;
	countries: any[];
}

const masks = {
	7: {
		mask: "(999) 999-99-99",
		label: "(___) ___-__-__",
	},
	0: {
		mask: "",
		label: "",
	},
} as {
	[key: number]: {
		mask: string;
		label: string;
	};
};

const Phone: React.FC<PhoneProps> = (props) => {
	const {
		model,
		bindingProperty,
		label = "",
		deletableField = false,
		disabled = false,
		countries = [],
	} = props;

	const [selectedMask, setSelectedMask] = useState<number>(7);
	const [modelValue, setModelValue] = useState<any>();

	const [showSelectCountry, setShowSelectCountry] = useState<boolean | null>(null);
	const [searchInputValue, setSearchInputValue] = useState<string>("");
	const [countryCode, setCountryCode] = useState<number | string | undefined>();
	const [number, setNumber] = useState<string>("");

	const searchInputElementRef = useRef<any>(null);
	const inputMaskRef = useRef<any>(null);
	const numberRef = useRef<any>(null);
	const countryCodeRef = useRef<any>(null);

	const parsePhoneData = useCallback((model: any, bindingProperty: string) => {
		const phone = model.get(bindingProperty);
		setCountryCode(phone.countryCode);
		setNumber(phone.cityCode + phone.number);
    console.log('phone',phone);
	}, []);

	const onInputMaskComplete = (value: any) => {
		const values = /\((.*)\) (.*)/.exec(value);
		const countryCode = countryCodeRef.current;

		model.set(bindingProperty, {
			countryCode: `${countryCode}`.replace(/\D/g, ""),
			cityCode:
				values !== null && countryCode === "7"
					? values[1].replace(/\D/g, "")
					: "",
			number:
				values !== null
					? values[2].replace(/\D/g, "")
					: `${value}`.replace(/\D/g, ""),
		});
		model.trigger(`change:${bindingProperty}`, model);

		setNumber(value);
	};

	// init input mask
	useEffect(() => {
		inputMaskRef.current = new Inputmask({
			mask: masks[selectedMask].mask,
			placeholder: masks[selectedMask].label,
			clearMaskOnLostFocus: false,
			clearIncomplete: true,
			minChars: 0,
			width: 100,
			oncomplete: (event: any) => onInputMaskComplete(event.target.value),
			onincomplete: (event: any) => {
				onInputMaskComplete(event.target.value);
			},
		});
		inputMaskRef.current.mask(numberRef.current);
	}, []);

	// bind model
	useEffect(() => {
		const onModelChanged = () => {
      console.log('onChange model', JSON.stringify(model.get(bindingProperty)))
			setModelValue(JSON.stringify(model.get(bindingProperty)));
		};
		model.on(`change:${bindingProperty}`, onModelChanged);
		return () => {
			model.off(`change:${bindingProperty}`, onModelChanged);
		};
	}, []);

	// from model to state
	useEffect(() => {
		if (model && model.has(bindingProperty)) {
			parsePhoneData(model, bindingProperty);
		}
	}, [model, bindingProperty, modelValue]);

	// apply mask
	useEffect(() => {
		const tryMask = masks[selectedMask] || masks[0];

		const mask = tryMask?.mask;
		const label = tryMask?.label;
		const dataInputmask = document.createAttribute("data-inputmask");

		dataInputmask.value = `'mask': '${mask}', 'placeholder' : '${label}'`;
		numberRef?.current?.attributes.setNamedItem(dataInputmask);

		numberRef?.current?.inputmask?.remove();
		inputMaskRef.current.mask(numberRef.current);
	}, [selectedMask]);

	useEffect(() => {
		if (countryCode) {
			if (!masks[countryCode as number] && selectedMask !== 0) {
				setSelectedMask(0);
			} else if (masks[countryCode as number]) {
				setSelectedMask(parseInt(`${countryCode}`));
			}
		}
		// save countryCode to ref for inputMask
		countryCodeRef.current = countryCode;
	}, [countryCode, selectedMask]);

	const sourceOptions = useMemo(
		() =>
			countries.map((country: any, index: number) => ({
				key: `${country.phoneCode}_${index}`,
				label: `${country.phoneCode ? `+${country.phoneCode}` : ""} ${country.caption}`,
				value: country.phoneCode || "",
			})),
		[],
	);
	const [showedOptions, setShowedOptions] = useState<any>([]);
	useEffect(() => {
		if (showSelectCountry) {
			setShowedOptions(sourceOptions);
			setTimeout(() => {
				searchInputElementRef.current.focus();
			}, 100);
		} else if (showSelectCountry !== null) {
      setTimeout(() => {
			  numberRef.current.focus();
      }, 100);
		}
		setSearchInputValue("");
	}, [showSelectCountry]);

	useEffect(() => {
		setShowedOptions(
			sourceOptions.filter((option) => {
				return (
					option.label
						.toLocaleLowerCase()
						.indexOf(searchInputValue.toLocaleLowerCase()) !== -1
				);
			}),
		);
	}, [searchInputValue]);

	useEffect(() => {
		const tryMask = masks[selectedMask] || masks[0];

		// handle if unmasked value
		if (tryMask.mask === "") {
			onInputMaskComplete(number);
		}
	}, [number]);

	const hiddenSelectStyles = {
		position: "absolute",
		opacity: 0,
		height: 0,
		width: 0,
		pointerEvents: "none",
		overflow: "hidden",
		zIndex: -1,
	};

	return (
		<div className="r-phone">
			<span className="b-input__label">{label}</span>
			<div className="b-input__value">
				<div className="r-phone__country">
					<div style={hiddenSelectStyles}>
						<Select
							open={showSelectCountry === true}
							showSearch
							dropdownClassName="rc-phone-dropdown"
							dropdownStyle={{
								width: 200,
							}}
							value={countryCode}
							onChange={(value: string) => {
								// save countryCode to ref for inputMask
								countryCodeRef.current = value;
								setCountryCode(value);
								onInputMaskComplete("");
							}}
							onDropdownVisibleChange={(open) => {
								setShowSelectCountry(open);
							}}
							dropdownRender={(menu) => {
								return (
									<div className="rc-phone-search-input">
										<input
											ref={searchInputElementRef}
											name="___inner___"
											onChange={(event) =>
												setSearchInputValue(event.target.value)
											}
											value={searchInputValue}
										/>
										{menu}
									</div>
								);
							}}
							options={showedOptions}
						/>
					</div>
					<span
						className="b-input-phone__country-visible"
						onClick={() => {
							if (!disabled) {
								setShowSelectCountry(true);
							}
						}}
					>
						+{countryCode || '__'}
					</span>
				</div>
				<div className="r-phone__number">
					<input
						ref={numberRef}
						className="b-input-phone__value"
						type="text"
						value={number}
						onChange={(event: any) => setNumber(event.target.value)}
						disabled={disabled}
					/>
				</div>
				{deletableField && <div className="b-input-phone__remove"></div>}
			</div>
		</div>
	);
};
export default Phone;
